import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Heading, Text, Button } from '../ui'
import type { LigacaoRelatorioRow } from '../../types/comercialContatosPortal'
import LigacaoInformacoesList from './LigacaoInformacoesList'
import {
  feedbackLabel,
  formatarDataHoraContato,
  isRelatorioRow,
  MeioContatoBadge,
  resolveContatoAtendido,
  TeveContatoBadge,
  TipoContatoBadge,
  type HistoricoContatosTableRow,
} from './historicoContatosShared'

type HistoricoContatoDetalheModalProps = {
  row: HistoricoContatosTableRow | null
  showCorretor?: boolean
  onClose: () => void
}

const HistoricoContatoDetalheModal: React.FC<HistoricoContatoDetalheModalProps> = ({
  row,
  showCorretor = false,
  onClose,
}) => {
  if (!row) return null

  const relatorioRow: LigacaoRelatorioRow | null = isRelatorioRow(row) ? row : null
  const contatoAtendido = resolveContatoAtendido(row)
  const exibirCorretor = showCorretor || Boolean(relatorioRow?.corretora_nome)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
          aria-hidden
        />
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="historico-contato-detalhe-titulo"
        >
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <div>
              <Heading as="h2" size="2xl" id="historico-contato-detalhe-titulo" className="text-gray-900 dark:text-white">
                Detalhes do contato
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {formatarDataHoraContato(row.data, row.horario)}
              </Text>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto space-y-4 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {showCorretor && (
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wide">
                    Comercial responsável
                  </Text>
                  <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                    {relatorioRow?.comercial_responsavel_nome || '—'}
                  </Text>
                </div>
              )}

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Responsável
                </Text>
                <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                  {row.registered_by_name?.trim() || '-'}
                </Text>
              </div>

              {exibirCorretor && (
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wide">
                    Corretor
                  </Text>
                  <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                    {relatorioRow?.corretora_nome || '—'}
                  </Text>
                </div>
              )}

              {exibirCorretor && (
                <div>
                  <Text size="xs" color="muted" className="uppercase tracking-wide">
                    ID SCGP
                  </Text>
                  <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                    {relatorioRow?.corretora_id_scgp != null ? String(relatorioRow.corretora_id_scgp) : '—'}
                  </Text>
                </div>
              )}

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Data e horário
                </Text>
                <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                  {formatarDataHoraContato(row.data, row.horario)}
                </Text>
              </div>

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Pessoa contactada
                </Text>
                {contatoAtendido ? (
                  <div>
                    <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                      {contatoAtendido.nome}
                    </Text>
                    {contatoAtendido.cargo && (
                      <Text size="xs" color="muted">
                        {contatoAtendido.cargo}
                      </Text>
                    )}
                  </div>
                ) : (
                  <Text size="sm" color="muted">
                    -
                  </Text>
                )}
              </div>

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide mb-1 block">
                  Meio de contato
                </Text>
                <MeioContatoBadge tipo={row.tipo_contato} />
              </div>

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide mb-1 block">
                  Tipo de contato
                </Text>
                <TipoContatoBadge tipo={row.tipo} />
              </div>

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide mb-1 block">
                  Sucesso no contato?
                </Text>
                <TeveContatoBadge teveContato={row.teve_contato} />
              </div>

              <div>
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Feedback geral
                </Text>
                <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                  {feedbackLabel(row.feedback_geral)}
                </Text>
              </div>

              <div className="sm:col-span-2">
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  Motivo
                </Text>
                <Text size="sm" weight="medium" className="text-gray-900 dark:text-white">
                  {row.motivo || '-'}
                </Text>
              </div>
            </div>

            <div>
              <Text size="xs" color="muted" className="uppercase tracking-wide mb-2 block">
                Informações
              </Text>
              <LigacaoInformacoesList lines={row.registros} />
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoricoContatoDetalheModal
